// frontend/src/hooks/useRunCostStats.ts
import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface RunCostStats {
  totalRuns: number;
  paidUserRuns: number;
  freeUserRuns: number;
  anonymousRuns: number;
  totalCostUsd?: string;  // Only present for monetized apps
  avgCostPerRun?: string;  // Only present for monetized apps
  totalRuntimeMs: number;
  avgRuntimeMs: string;
  periodDays: number;
  isMonetized: boolean;
}

export interface RecentRun {
  id: string;
  runtime_ms: number;
  cost_usd: string;
  user_subscription_tier: string | null;
  created_at: string;
  timed_out: boolean;
}

interface UseRunCostStatsResult {
  stats: RunCostStats | null;
  recentRuns: RecentRun[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

interface UseRunCostStatsOptions {
  enabled?: boolean;
}

export function useRunCostStats(
  projectId: string | undefined,
  days: number = 7,
  options?: UseRunCostStatsOptions
): UseRunCostStatsResult {
  const { session, authReady, ensureFreshToken } = useAuth();
  const enabled = options?.enabled ?? true;
  const [stats, setStats] = useState<RunCostStats | null>(null);
  const [recentRuns, setRecentRuns] = useState<RecentRun[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState(false);

  // Keep auth helpers in refs so fetchStats identity remains stable.
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const ensureFreshTokenRef = useRef(ensureFreshToken);
  useEffect(() => {
    ensureFreshTokenRef.current = ensureFreshToken;
  }, [ensureFreshToken]);

  // If user identity or project changes, clear sticky access-denied state.
  useEffect(() => {
    setAccessDenied(false);
  }, [projectId, session?.user?.id]);

  const fetchStats = useCallback(async (options?: { force?: boolean }) => {
    if (!projectId || !enabled) return;
    if (accessDenied && !options?.force) return;
    
    // Wait for auth to be ready before fetching
    const currentSession = sessionRef.current;
    if (!authReady || !currentSession) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      let token = currentSession.access_token;
      let response = await fetch(
        `${supabaseUrl}/functions/v1/get-run-cost-stats?projectId=${projectId}&days=${days}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      // Handle stale session during auth bootstrap; refresh once and retry.
      if (response.status === 401) {
        const refreshed = await ensureFreshTokenRef.current({ forceRefresh: true });
        if (refreshed) {
          token = refreshed;
          response = await fetch(
            `${supabaseUrl}/functions/v1/get-run-cost-stats?projectId=${projectId}&days=${days}`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
              },
            }
          );
        }
      }

      if (response.status === 403) {
        setAccessDenied(true);
        setStats(null);
        setRecentRuns([]);
        setError('Access denied');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch cost stats');
      }

      const data = await response.json();
      setStats(data.stats);
      setRecentRuns(data.recentRuns || []);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to fetch cost stats';
      if (message !== 'Access denied') {
        console.error('Error fetching cost stats:', err);
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [projectId, days, authReady, accessDenied, enabled]);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchStats();
  }, [fetchStats, enabled]);

  const refetch = useCallback(async () => {
    setAccessDenied(false);
    await fetchStats({ force: true });
  }, [fetchStats]);

  return {
    stats,
    recentRuns,
    loading,
    error,
    refetch,
  };
}
